// Utility functions for piece management and sorting

/**
 * Calculate popularity score based on various metrics
 * @param {Object} piece - The piece object
 * @returns {number} - Popularity score (higher = more popular)
 */
export const calculatePopularityScore = (piece) => {
  const views = piece.views || 0;
  const likes = piece.likes || 0;
  const downloads = piece.downloads || 0;
  const sales = piece.sold || 0;
  const totalMade = piece.totalMade || 1;
  
  // Weighted scoring system
  const viewScore = views * 0.1;
  const likeScore = likes * 2;
  const downloadScore = downloads * 5;
  const salesScore = sales * 10;
  const rarityScore = (totalMade - sales) / totalMade * 20; // Higher score for rarer items
  
  return viewScore + likeScore + downloadScore + salesScore + rarityScore;
};

/**
 * Check if a piece is considered "new" (created within last 7 days)
 * @param {Object} piece - The piece object
 * @returns {boolean} - True if piece is new
 */
export const isNewPiece = (piece) => {
  const createdAt = new Date(piece.createdAt || piece.createdDate);
  const now = new Date();
  const daysDiff = (now - createdAt) / (1000 * 60 * 60 * 24);
  return daysDiff <= 7;
};

/**
 * Check if a piece is considered "trending" (high activity in last 24 hours)
 * @param {Object} piece - The piece object
 * @returns {boolean} - True if piece is trending
 */
export const isTrendingPiece = (piece) => {
  const popularityScore = calculatePopularityScore(piece);
  return popularityScore > 50; // Threshold for trending
};

/**
 * Sort pieces by popularity (highest first)
 * @param {Array} pieces - Array of piece objects
 * @returns {Array} - Sorted array
 */
export const sortByPopularity = (pieces) => {
  return [...pieces].sort((a, b) => {
    const scoreA = calculatePopularityScore(a);
    const scoreB = calculatePopularityScore(b);
    return scoreB - scoreA;
  });
};

/**
 * Sort pieces by newest first
 * @param {Array} pieces - Array of piece objects
 * @returns {Array} - Sorted array
 */
export const sortByNewest = (pieces) => {
  return [...pieces].sort((a, b) => {
    const dateA = new Date(a.createdAt || a.createdDate);
    const dateB = new Date(b.createdAt || b.createdDate);
    return dateB - dateA;
  });
};

/**
 * Get popular pieces (top 20% by popularity score)
 * @param {Array} pieces - Array of piece objects
 * @returns {Array} - Array of popular pieces
 */
export const getPopularPieces = (pieces) => {
  const sorted = sortByPopularity(pieces);
  const topCount = Math.max(1, Math.ceil(sorted.length * 0.2));
  return sorted.slice(0, topCount);
};

/**
 * Get new pieces (created within last 7 days)
 * @param {Array} pieces - Array of piece objects
 * @returns {Array} - Array of new pieces
 */
export const getNewPieces = (pieces) => {
  return pieces.filter(isNewPiece).sort((a, b) => {
    const dateA = new Date(a.createdAt || a.createdDate);
    const dateB = new Date(b.createdAt || b.createdDate);
    return dateB - dateA;
  });
};

/**
 * Get trending pieces (high popularity score)
 * @param {Array} pieces - Array of piece objects
 * @returns {Array} - Array of trending pieces
 */
export const getTrendingPieces = (pieces) => {
  return pieces.filter(isTrendingPiece).sort((a, b) => {
    const scoreA = calculatePopularityScore(a);
    const scoreB = calculatePopularityScore(b);
    return scoreB - scoreA;
  });
};

/**
 * Format price with USD conversion
 * @param {number} solPrice - Price in SOL
 * @param {number} solToUsd - SOL to USD conversion rate
 * @returns {string} - Formatted price string
 */
export const formatPrice = (solPrice, solToUsd = 150) => {
  const usdPrice = solPrice * solToUsd;
  return `${solPrice} SOL ($${usdPrice.toFixed(2)})`;
};

/**
 * Get category display name
 * @param {string} category - Category key
 * @returns {string} - Display name
 */
export const getCategoryDisplayName = (category) => {
  const categoryMap = {
    'tops': 'Tops',
    'bottoms': 'Bottoms', 
    'shoes': 'Shoes',
    'hats': 'Hats',
    'necklaces': 'Necklaces',
    'belts': 'Belts',
    'watches': 'Watches',
    'other': 'Other'
  };
  return categoryMap[category] || category;
};
